import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Rocket } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  createMpBuyoutInBp,
  createMpBuyoutInUnf,
  getMpBuyoutPreview,
} from "@/api/marketplace_buyouts";
import { useConnection } from "@/auth/ConnectionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatRub } from "@/lib/format";
import { extractErrorMessage } from "@/lib/api-errors";
import type {
  MpBuyoutBpResolution,
  MpBuyoutCreateResponse,
  MpBuyoutDocumentRef,
  MpBuyoutMissingProduct,
  OneCConnectionView,
} from "@/types/api";

const BP_CONN_STORAGE_KEY = "mp-buyout-bp-conn";
const UNF_CONN_STORAGE_KEY = "mp-buyout-unf-conn";

const BASE_LABEL: Record<string, string> = {
  unf: "УНФ · Расходная накладная",
  bp: "Бухгалтерия · Реализация товаров и услуг",
};

export function MarketplaceBuyoutPreviewPage(): React.ReactElement {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { activeConnectionId: connId, connections } = useConnection();
  const [buyoutDate, setBuyoutDate] = useState<string>("");

  const unfConns = useMemo(
    () => connections.filter((c) => c.config_type === "unf"),
    [connections],
  );
  const bpConns = useMemo(
    () => connections.filter((c) => c.config_type === "bp"),
    [connections],
  );

  const [unfConnId, setUnfConnId] = useState<string>("");
  const [bpConnId, setBpConnId] = useState<string>("");

  // Дефолт УНФ — последнее выбранное → активное (если УНФ) → первое УНФ.
  useEffect(() => {
    if (unfConnId || unfConns.length === 0) return;
    const saved = localStorage.getItem(UNF_CONN_STORAGE_KEY);
    const fromSaved = unfConns.find((c) => c.id === saved);
    const fromActive = unfConns.find((c) => c.id === connId);
    setUnfConnId((fromSaved ?? fromActive ?? unfConns[0]!).id);
  }, [unfConns, connId, unfConnId]);

  // Дефолт БП — последнее выбранное → подключение «по умолчанию» → первое.
  useEffect(() => {
    if (bpConnId || bpConns.length === 0) return;
    const saved = localStorage.getItem(BP_CONN_STORAGE_KEY);
    const fromSaved = bpConns.find((c) => c.id === saved);
    const fromDefault = bpConns.find((c) => c.is_default);
    setBpConnId((fromSaved ?? fromDefault ?? bpConns[0]!).id);
  }, [bpConns, bpConnId]);

  // Сохраняем выбор сразу — чтобы он подхватывался при следующем заходе,
  // даже если в этот раз создание не получилось.
  const onUnfConnChange = (next: string): void => {
    setUnfConnId(next);
    if (next) localStorage.setItem(UNF_CONN_STORAGE_KEY, next);
  };
  const onBpConnChange = (next: string): void => {
    setBpConnId(next);
    if (next) localStorage.setItem(BP_CONN_STORAGE_KEY, next);
  };

  const previewQ = useQuery({
    queryKey: ["mp-buyout-preview", id, connId, unfConnId, bpConnId],
    queryFn: () =>
      getMpBuyoutPreview(id, {
        unfConnectionId: unfConnId || undefined,
        bpConnectionId: bpConnId || undefined,
      }),
    enabled: !!id && !!connId,
  });

  // Дефолт даты документа — дата уведомления из файла.
  useEffect(() => {
    if (previewQ.data?.suggested_date && !buyoutDate) {
      setBuyoutDate(previewQ.data.suggested_date);
    }
  }, [previewQ.data, buyoutDate]);

  const onCreated = (resp: MpBuyoutCreateResponse, base: string): void => {
    qc.invalidateQueries({ queryKey: ["mp-buyout-preview", id, connId] });
    qc.invalidateQueries({ queryKey: ["mp-buyouts", connId] });
    const doc = resp.documents.find((d) => d.base === base);
    if (doc) {
      const posted = doc.posted ? "проведён" : "НЕ проведён — допроведите вручную";
      toast.success(`Документ № ${doc.one_c_number ?? "?"} создан (${posted})`);
    }
  };

  const onCreateError = (err: unknown): void => {
    const detail =
      (err as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail ?? String(err);
    toast.error("Не удалось создать документ", { description: detail });
  };

  const createUnfMut = useMutation({
    mutationFn: () =>
      createMpBuyoutInUnf(id, {
        buyout_date: buyoutDate,
        connection_id: unfConnId,
      }),
    onSuccess: (resp) => onCreated(resp, "unf"),
    onError: onCreateError,
  });

  const createBpMut = useMutation({
    mutationFn: () =>
      createMpBuyoutInBp(id, {
        buyout_date: buyoutDate,
        connection_id: bpConnId,
      }),
    onSuccess: (resp) => onCreated(resp, "bp"),
    onError: onCreateError,
  });

  const p = previewQ.data;
  const docByBase = (base: string): MpBuyoutDocumentRef | undefined =>
    p?.created_documents.find((d) => d.base === base);

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/mp-buyouts")}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          К списку
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          Уведомление о выкупе
        </h1>
      </header>

      {previewQ.isLoading && (
        <p className="text-sm text-muted-foreground">Загрузка…</p>
      )}
      {previewQ.isError && (
        <p className="text-sm text-rose-600">
          Не удалось загрузить: {extractErrorMessage(previewQ.error, "нет ответа")}
        </p>
      )}

      {p && p.status === "error" && (
        <div className="rounded-md border border-rose-300 bg-rose-50 p-4 text-sm text-rose-800">
          Ошибка парсинга файла: {p.parse_error}
        </div>
      )}

      {p && p.status !== "error" && (
        <>
          <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
            <Field label="Организация" value={p.organization_name} />
            <Field
              label="Контрагент"
              value={p.kontragent_name ?? "— не найден —"}
            />
            <Field label="Уведомление №" value={p.notice_number ?? "—"} />
            <Field
              label="Позиций / сумма"
              value={`${p.lines.length} · ${formatRub(p.totals.sum)}`}
            />
          </div>

          {p.missing.length > 0 && (
            <MissingBlock title="Не сопоставлено в УНФ" items={p.missing} />
          )}

          <UnfIssuesBlock
            kontragentFound={!!p.kontragent_key}
            templateFound={p.template_found}
            lineCount={p.lines.length}
          />

          {p.bp && (
            <BpIssuesBlock bp={p.bp} />
          )}

          {/* Создание документов — одно окно, по действию на базу */}
          <div className="space-y-3 rounded-md border p-4">
            <div className="flex items-end gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="bdate">Дата документа</Label>
                <Input
                  id="bdate"
                  type="date"
                  value={buyoutDate}
                  onChange={(e) => setBuyoutDate(e.target.value)}
                  className="w-44"
                />
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <BaseCard
                base="unf"
                doc={docByBase("unf")}
                connections={unfConns}
                connId={unfConnId}
                onConnChange={onUnfConnChange}
                disabled={!p.is_ready || !buyoutDate || !unfConnId}
                pending={createUnfMut.isPending}
                onCreate={() => createUnfMut.mutate()}
              />
              <BaseCard
                base="bp"
                doc={docByBase("bp")}
                connections={bpConns}
                connId={bpConnId}
                onConnChange={onBpConnChange}
                disabled={
                  !buyoutDate ||
                  !bpConnId ||
                  // БП-резолв ещё не запрашивался (нет bpConnId) — кнопку
                  // блокируем; иначе блокируем, если БП не готова.
                  !p.bp ||
                  !p.bp.is_ready
                }
                pending={createBpMut.isPending}
                onCreate={() => createBpMut.mutate()}
              />
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Артикул</TableHead>
                <TableHead>Номенклатура</TableHead>
                <TableHead>Характеристика</TableHead>
                <TableHead className="text-right">Кол-во</TableHead>
                <TableHead className="text-right">Цена</TableHead>
                <TableHead className="text-right">Сумма</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {p.lines.map((l) => (
                <TableRow key={l.nom_ref}>
                  <TableCell className="text-xs">
                    {l.sample_artikul ?? "—"}
                  </TableCell>
                  <TableCell>{l.nom_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {l.char_name ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">{l.quantity}</TableCell>
                  <TableCell className="text-right">{formatRub(l.price)}</TableCell>
                  <TableCell className="text-right">
                    {formatRub(l.sum_total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
}

function BaseCard({
  base,
  doc,
  connections,
  connId,
  onConnChange,
  disabled,
  pending,
  onCreate,
}: {
  base: string;
  doc: MpBuyoutDocumentRef | undefined;
  connections: OneCConnectionView[];
  connId: string;
  onConnChange: (id: string) => void;
  disabled: boolean;
  pending: boolean;
  onCreate: () => void;
}): React.ReactElement {
  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
      <p className="text-sm font-medium">{BASE_LABEL[base] ?? base}</p>
      {doc ? (
        <div className="flex items-start gap-2 text-sm text-emerald-700">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div>
              Создан: № {doc.one_c_number ?? "?"}{" "}
              {doc.posted ? "(проведён)" : "(не проведён — допроведите вручную)"}
            </div>
            <div className="text-xs text-emerald-700/80">
              База: {doc.connection_name ?? "— неизвестно —"}
            </div>
          </div>
        </div>
      ) : connections.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Нет подключения к этой базе — добавьте его в профиле.
        </p>
      ) : (
        <>
          <select
            value={connId}
            onChange={(e) => onConnChange(e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
          >
            {connections.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            className="w-full"
            disabled={disabled || pending}
            onClick={onCreate}
          >
            <Rocket className="mr-2 h-4 w-4" />
            {pending
              ? "Создание…"
              : base === "unf"
                ? "Создать в УНФ"
                : "Создать в БП"}
          </Button>
        </>
      )}
    </div>
  );
}

function MissingBlock({
  title,
  items,
}: {
  title: string;
  items: MpBuyoutMissingProduct[];
}): React.ReactElement {
  return (
    <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
      <p className="font-medium">
        {title}: {items.length}
      </p>
      <p className="text-xs">
        Артикулы должны совпадать с реквизитом «Артикул» номенклатуры в 1С:
      </p>
      <ul className="mt-1 list-inside list-disc text-xs">
        {items.map((m, i) => (
          <li key={i}>
            {m.artikul ?? "—"} — {m.name ?? "без названия"} ({m.rows} стр.)
          </li>
        ))}
      </ul>
    </div>
  );
}

function UnfIssuesBlock({
  kontragentFound,
  templateFound,
  lineCount,
}: {
  kontragentFound: boolean;
  templateFound: boolean;
  lineCount: number;
}): React.ReactElement | null {
  const issues: string[] = [];
  if (lineCount === 0) {
    issues.push("В уведомлении не распознано ни одной строки.");
  }
  if (!kontragentFound) {
    issues.push(
      "Контрагент-маркетплейс не найден в УНФ — заведите его в справочнике контрагентов.",
    );
  }
  if (kontragentFound && !templateFound) {
    issues.push(
      "В УНФ нет ни одной «Расходной накладной» с операцией «Выкуп товаров комиссионером» для этой пары (организация × контрагент) — заведите один документ вручную, и система запомнит настройки шапки.",
    );
  }
  if (issues.length === 0) return null;
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
      <p className="font-medium">Проблемы для создания в УНФ</p>
      <ul className="mt-1 list-inside list-disc text-xs">
        {issues.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ul>
    </div>
  );
}

function BpIssuesBlock({
  bp,
}: {
  bp: MpBuyoutBpResolution;
}): React.ReactElement | null {
  const issues: string[] = [];
  if (!bp.organization_found) {
    issues.push(
      "Организация не найдена в БП (по ИНН). Проверьте, что та же организация заведена в БП.",
    );
  }
  if (!bp.kontragent_found) {
    issues.push(
      "Контрагент-маркетплейс не найден в БП (по ИНН).",
    );
  }
  if (bp.organization_found && bp.kontragent_found && !bp.template_found) {
    issues.push(
      "В БП нет ни одной «Реализации товаров и услуг» с операцией «Выкуп товаров комиссионером» для этой пары — заведите один документ вручную, и система запомнит настройки.",
    );
  }
  const hasMissing = bp.missing.length > 0;
  if (issues.length === 0 && !hasMissing) return null;
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
      <p className="font-medium">Проблемы для создания в БП</p>
      {issues.length > 0 && (
        <ul className="mt-1 list-inside list-disc text-xs">
          {issues.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
      )}
      {hasMissing && (
        <div className="mt-2">
          <p className="text-xs font-medium">
            Не сопоставлено в БП: {bp.missing.length}
          </p>
          <ul className="mt-1 list-inside list-disc text-xs">
            {bp.missing.map((m, i) => (
              <li key={i}>
                {m.artikul ?? "—"} — {m.name ?? "без названия"} ({m.rows} стр.)
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.ReactElement {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  );
}
